/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */

// src/hooks/useThreeScene.js
/**
 * @file useThreeScene.js
 * @description
 *   React hook to initialize and manage a Three.js scene, camera, renderer, OrbitControls,
 *   and animation loop, with support for both standard (desktop) and WebXR (VR) rendering.
 *   Handles proper cleanup, helpers, and exposes imperative controls for VR switching.
 *
 *   Key design:
 *   - Uses a standard requestAnimationFrame loop for desktop rendering.
 *   - When entering VR (WebXR), disables the standard loop and expects the parent to use
 *     renderer.setAnimationLoop for XR rendering (Three.js best practice).
 *   - Provides refs to scene, camera, renderer, controls, helpers, and camera rig.
 *   - Exposes imperative methods for starting/stopping animation and toggling VR mode.
 *
 *   Usage:
 *     const {
 *       sceneRef, cameraRef, rendererRef, controlsRef, axesHelperRef, polarGridRef, rigRef,
 *       startAnimation, stopAnimation, setInVR, isInVRRef
 *     } = useThreeScene(canvasRef, { onReady });
 *
 *   When entering VR, call setInVR(true) and use renderer.setAnimationLoop for XR.
 *   When exiting VR, call setInVR(false) to resume standard animation.
 */

import { useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { CustomAxesHelper } from '../objects/CustomAxesHelper';


/**
 * useThreeScene
 *
 * Initializes a Three.js scene, camera, renderer, OrbitControls, helpers, and manages the animation loop.
 * Handles both standard (desktop) and WebXR (VR) rendering modes.
 *
 * @param {React.RefObject<HTMLCanvasElement>} canvasRef - Ref to the canvas element for rendering.
 * @param {Object} [options]
 * @param {Function} [options.onReady] - Optional callback fired with { scene, camera, renderer } after setup.
 * @returns {Object} - Refs and imperative controls for the scene and animation.
 */
export function useThreeScene(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  { onReady }: { onReady?: (x: { scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer }) => void } = {}
) {
  // --- Three.js object refs ---
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const requestAnimationFrameRef = useRef<number | null>(null);
  const axesHelperRef = useRef<CustomAxesHelper | null>(null);
  const polarGridHelperRef = useRef<THREE.Object3D | null>(null);
  const rigRef = useRef<THREE.Group | null>(null);

  // Track if we're in VR mode (WebXR)
  // When true, disables the standard animation loop; expects parent to use renderer.setAnimationLoop
  const isInVRRef = useRef<boolean>(false);

  /**
   * Starts the standard (desktop) animation loop using requestAnimationFrame.
   * No-op if in VR mode.
   */
  const startAnimation = () => {
    if (!isInVRRef.current) {
      animate();
    }
  };

  /**
   * Stops the standard animation loop.
   * Safe to call even if not running.
   */
  const stopAnimation = () => {
    if (requestAnimationFrameRef.current != null) {
      cancelAnimationFrame(requestAnimationFrameRef.current);
    }
  };

  /**
   * Sets VR mode flag. When entering VR, stops the standard loop (parent should use renderer.setAnimationLoop).
   * When exiting VR, resumes the standard loop.
   * @param {boolean} inVR - True if entering VR/WebXR, false if returning to desktop.
   */
  const setInVR = (inVR) => {
    isInVRRef.current = inVR;
    if (inVR) {
      // Stop standard animation loop; parent should use renderer.setAnimationLoop for XR
      stopAnimation();
    } else {
      // Resume standard animation loop
      startAnimation();
    }
  };

  // --- Animation function for standard (desktop) rendering ---
  // This is NOT used in VR/WebXR mode; see setInVR above.
  let autoAngle = 0;
  function animate() {
    if (isInVRRef.current) return; // Don't run if in VR
    requestAnimationFrameRef.current = requestAnimationFrame(animate);
    autoAngle += 0.00025;
    // Optionally auto-orbit the camera rig
    if (rigRef.current) rigRef.current.rotation.y = autoAngle;
    if (controlsRef.current) controlsRef.current.update();
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  }

  useLayoutEffect(() => {
    // --- Scene and Camera Setup ---
    const canvas = canvasRef.current;
    if (!canvas) return;

    const baseDimension = 1.0;

    // 1) Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    //scene.background = new THREE.Color(0xffffff); // Set background to white
    console.log("[useThreeScene] Creating new Three.js scene:", scene);
    sceneRef.current = scene;

    // 2) Camera
    // Set near/far planes for VR and large scenes
    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.01, // Lower near plane to avoid near clipping
      5000  // Increase far plane to avoid far clipping
    );
    const positionScale = 0.75;
    camera.position.set(0.0, 1.0 * positionScale, 3.0 * positionScale);

    // 3) Camera rig (for optional auto‐orbit)
    const cameraRig = new THREE.Group();
    cameraRig.add(camera);
    scene.add(cameraRig);
    rigRef.current = cameraRig;
    cameraRef.current = camera;

    // 4) Renderer
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    rendererRef.current = renderer;

    // 5) OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controlsRef.current = controls;

    // 6) Lighting
    const ambient = new THREE.AmbientLight(0x404040);
    console.log("[useThreeScene] Adding AmbientLight to scene:", ambient);
    scene.add(ambient);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(10, 10, 10);
    console.log("[useThreeScene] Adding DirectionalLight to scene:", dirLight);
    scene.add(dirLight);

    // 7) Helpers
    // Polar grid helper (ground plane)
    const radius = baseDimension;
    // const sectors = 16;
    // const rings = baseDimension * 2;
    // const divisions = 64;
    // const polarGrid = new THREE.PolarGridHelper(
    //   radius, // radius of the grid
    //   sectors, // number of sectors (radial divisions)
    //   rings, // number of rings (circular divisions)
    //   divisions, // number of segments in circle
    //   0x888800, // radial line color
    //   0xffff00 // circular line color
    // );
    // polarGrid.material.transparent = true;
    // polarGrid.material.opacity = 0.375;
    // console.log("[useThreeScene] Adding PolarGridHelper to scene:", polarGrid);
    // scene.add(polarGrid);
    // polarGridHelperRef.current = polarGrid;

    // Custom axes helper
    // set up the custom axes helper with specific parameters
    // it expects an array of entities with axis, start, end, width, color, startColor, and endColor properties
    // prepare 6 entities, one for each positive and negative axis
    const endDistance = baseDimension;
    const startDistance = baseDimension * .125;
    const startColorDefault = 0x888888; // Default start color for axes
    const endColorDefault = 0x000000;
    const axesHelper = new CustomAxesHelper([
      { axis: 'x', positive: true, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
      { axis: 'x', positive: false, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
      { axis: 'y', positive: true, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
      { axis: 'y', positive: false, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
      { axis: 'z', positive: true, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
      { axis: 'z', positive: false, start: startDistance, end: endDistance, width: 0, startColor: startColorDefault, endColor: endColorDefault },
    ]);
    console.log("[useThreeScene] Adding CustomAxesHelper to scene:", axesHelper);
    scene.add(axesHelper);
    axesHelperRef.current = axesHelper;

    // Disable frustum culling for all objects in the scene (for debugging)
    scene.traverse((obj) => { obj.frustumCulled = false; });

    // --- Animation Loop (desktop only) ---
    // Start the animation loop (non-VR)
    startAnimation();

    // --- Resize handler ---
    function onResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }
    window.addEventListener("resize", onResize);

    // fire onReady callback if provided
    if (onReady) onReady({ scene, camera, renderer });

    // --- Cleanup ---
    return () => {
      stopAnimation();
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();
    };
  }, []); // <--- empty array, so it only runs once

  // --- Return refs and imperative controls ---
  return {
    sceneRef,           // Ref to THREE.Scene
    cameraRef,          // Ref to THREE.PerspectiveCamera
    rendererRef,        // Ref to THREE.WebGLRenderer
    controlsRef,        // Ref to OrbitControls
    axesHelperRef,      // Ref to AxesHelper
    polarGridHelperRef, // Ref to PolarGridHelper
    rigRef,             // Ref to camera rig (THREE.Group)
    startAnimation,     // Start standard animation loop
    stopAnimation,      // Stop standard animation loop
    setInVR,            // Set VR mode (see docs above)
    isInVRRef,          // Ref<boolean> for current VR state
  };
}
